import {
  capitalize,
  isPlainObject,
  upperFirst,
  transform,
  isEqual,
  isObject,
  debounce,
} from 'lodash';
import {
  parseDomain,
  ParseResultType,
} from 'parse-domain';
import { enGB as dateFnsLocaleEnGb } from 'date-fns/locale';

import * as inputCharsLimiters from '@/inputCharsLimiters/common';
import {
  TOPCASHBACK_PARTNER,
  TOPCASHBACK_TEST_PARTNER,
  TOTALLYMONEY_PARTNER,
  PAYINGTOOMUCH_PARTNER,
  PARTNERS,
  QZ_AFFILIATE_PREFIX,
  TOP_LEVEL_DOMAIN_TYPOS,
} from '@/constants/common';

import { UtcDate } from '@/helpers/date';

const debounceTimeout = 300;

// eslint-disable-next-line no-control-regex
export const emailRegex = /^(?!(?:(?:\x22?\x5C[\x00-\x7E]\x22?)|(?:\x22?[^\x5C\x22]\x22?)){255,})(?!(?:(?:\x22?\x5C[\x00-\x7E]\x22?)|(?:\x22?[^\x5C\x22]\x22?)){65,}@)(?:(?:[\x21\x23-\x27\x2A\x2B\x2D\x2F-\x39\x3D\x3F\x5E-\x7E]+)|(?:\x22(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21\x23-\x5B\x5D-\x7F]|(?:\x5C[\x00-\x7F]))*\x22))(?:\.(?:(?:[\x21\x23-\x27\x2A\x2B\x2D\x2F-\x39\x3D\x3F\x5E-\x7E]+)|(?:\x22(?:[\x01-\x08\x0B\x0C\x0E-\x1F\x21\x23-\x5B\x5D-\x7F]|(?:\x5C[\x00-\x7F]))*\x22)))*@(?:(?:(?!.*[^.]{64,})(?:(?:(?:xn--)?[a-z0-9]+(?:-+[a-z0-9]+)*\.){1,126}){1,}(?:(?:[a-z][a-z0-9]*)|(?:(?:xn--)[a-z0-9]+))(?:-+[a-z0-9]+)*)|(?:\[(?:(?:IPv6:(?:(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){7})|(?:(?!(?:.*[a-f0-9][:\]]){7,})(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,5})?::(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,5})?)))|(?:(?:IPv6:(?:(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){5}:)|(?:(?!(?:.*[a-f0-9]:){5,})(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,3})?::(?:[a-f0-9]{1,4}(?::[a-f0-9]{1,4}){0,3}:)?)))?(?:(?:25[0-5])|(?:2[0-4][0-9])|(?:1[0-9]{2})|(?:[1-9]?[0-9]))(?:\.(?:(?:25[0-5])|(?:2[0-4][0-9])|(?:1[0-9]{2})|(?:[1-9]?[0-9]))){3}))\]))$/i;
export const postcodeRegex = /^[a-zA-Z0-9 ]+$/;
export const telephoneRegex = /^(0|(\+44))(1|2|3|7)\d+$/;
export const telephoneLeadgenRegex = /^(0|(\+44))(1|2|3|7|8)\d+$/;
export const nameRegex = /^([A-Za-z-\s]|[À-Ö]|[Ø-ö]|[ø-ǿ]|[Ȁ-ʯ]|[̀-ͯ]|[Ḁ-ỿ]|[Ⅰ-ↈ])+$/;
export const passwordRegex = /^[^\s].*[^\s]$/;

// build helpers
export const createCssVarRgbColors = colors => {
  function hexToRgb (hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ] : null;
  }

  Object.keys(colors).forEach(colorName => {
    const rgb = hexToRgb(colors[colorName]);
    if (rgb) {
      document.querySelector(':root').style.setProperty(`--v-${colorName}-rgb`, rgb.join(', '));
    }
  });
};

export const getPartnerFonts = config => {
  return [1, 2, 3].map(fontNumber => {
    return config[`partnerFont${fontNumber}`];
  });
};

export const getPartnerFontsWebfontsConfig = fonts => {
  const config = {};

  fonts.forEach(font => {
    if (!font) return;
    switch (font.type) {
      case 'Google':
        if (!config.google) config.google = { families: [] };
        config.google.families.push(`${font.name}:${font.weight}`);
        break;
      case 'custom':
        if (!config.custom) config.custom = { families: [], urls: [] };
        config.custom.families.push(font.name);
        config.custom.urls.push(font.url);
    }
  });
  return Object.keys(config).length > 0 ? config : null;
};

export const partnerFooters = {
  // unique footers per partners
  '8ccd42a8b26f15ceb5612106ea0f41fe': () => import('@/components/car/partners/totallymoney/Footer.vue'),
  '9a70c66c0d62f5a4b6dbfab6b047fc2f': () => import('@/components/car/partners/payingtoomuch/Footer.vue'),

  // Quotezone unique footer
  newQuotezoneResponsive: () => import('@/components/car/partners/newQuotezoneResponsive/Footer.vue'),
  quotezonenew: () => import('@/components/car/partners/newQuotezoneResponsive/Footer.vue'),

  // footers for WLS
  custom: () => import('@/components/whitelabels/Footers/FooterCustom.vue'),
  customIAR: () => import('@/components/whitelabels/Footers/FooterIAR.vue'),
  customAR: () => import('@/components/whitelabels/Footers/FooterAR.vue'),
};

export const getPartnerHeaderComponent = (config, productName) => {
  // Custom VAN and BIKE headers for TopCashBack partner
  const isTopCashBackPartner = [
    TOPCASHBACK_PARTNER,
    TOPCASHBACK_TEST_PARTNER,
  ].includes(config.partner);
  const isProductWithCustomHeader = ['van', 'bike', 'pet', 'travel'].includes(productName);

  if (isTopCashBackPartner && isProductWithCustomHeader) {
    return () => import('@/components/whitelabels/Headers/HeaderTCB.vue');
  }
  if (PARTNERS.Apricot.includes(config.partner)) {
    return () => import('@/components/whitelabels/Headers/HeaderApricot.vue');
  }

  return () => import('@/components/SiteHeader/SiteHeader.vue');
};

export const getPartnerFooterComponent = config => {
  if (!config.partnerFooter) return null;

  // Only 3 partners have unique footers
  const isPartnerWithUniqueFooter = [
    ...PARTNERS.QuoteZone,
    TOTALLYMONEY_PARTNER,
    PAYINGTOOMUCH_PARTNER,
  ].includes(config.partner);

  const isAffiliateWithCustomFooter = PARTNERS.QuoteZone.includes(config.partner) &&
    config.affiliateCompanyName?.length;

  const isQzAffiliate = config.partner.startsWith(QZ_AFFILIATE_PREFIX);

  // show FooterCustom for affiliates
  if (isAffiliateWithCustomFooter) {
    return partnerFooters.custom;
  }

  if (isPartnerWithUniqueFooter || isQzAffiliate) {
    return partnerFooters[config.partner] || partnerFooters[config.partnerParent];
  }

  if (['AR', 'IAR'].includes(config.partnerFooterText)) {
    return partnerFooters[`custom${config.partnerFooterText}`];
  }

  return partnerFooters.custom;
};

// api helpers
export const minWait = async (promise, ms = 250) => {
  const [response] = await Promise.all([
    promise,
    new Promise(resolve => setTimeout(resolve, ms)),
  ]);
  return response;
};
export const wait = async ms => new Promise((resolve) => setTimeout(() => resolve(true), ms));
export const debounced = func => debounce(func, debounceTimeout);

export const getPlaceholderResponsiveText = (text, isMobile) => {
  if (!isMobile) return text;
  if (text.indexOf('Please ') !== 0) return text;
  return capitalize(text.replace('Please ', ''));
};

// inputs limiters helpers
export const limitValueChars = (value, maxChars = 3) => {
  return () => {
    if (!value) return value;
    return value.length > maxChars ? value.substring(0, maxChars) : value;
  };
};
export const limitInputChars = function (step, value, limit) {
  step[value] = (limitValueChars(step[value], limit))();
};
export const removeNewLineChars = function (step, value) {
  step[value] = step[value].replace(/[\r\n]/g, ' ');
};
/**
 * Used to prevent input character collapsing
 * @param insuredPhone
 * @returns {number}
 */
export const phoneNumberLimit = insuredPhone => {
  const limit = inputCharsLimiters.TELEPHONE;

  if (!insuredPhone) return limit - 1;

  return insuredPhone.indexOf('+44') === 0 ? limit + 2 : limit;
};

// options helpers
export const monthsOptions = [...Array(12)].map((_, i) => ({
  value: i + 1,
  text: dateFnsLocaleEnGb.localize.month(i),
}));

export const createOptions = ({
  source,
  valueKey = 'value',
  textKey = 'text',
  asNumbers,
}) => {
  const isArray = Array.isArray(source);

  if (
    isArray &&
    source.every(
      (item) =>
        item &&
        isPlainObject(item) &&
        Object.prototype.hasOwnProperty.call(item, valueKey) &&
        Object.prototype.hasOwnProperty.call(item, textKey),
    )
  ) {
    return source.map((item) => ({
      ...item,
      [valueKey]:
        asNumbers && /^\d+$/.test(item[valueKey])
          ? +item[valueKey]
          : item[valueKey],
    }));
  }

  return Object.keys(source).map((key) => {
    const value = isArray ? source[key] : key;
    const text = isPlainObject(source[key]) ? source[key][textKey] : source[key];
    const item = {
      [valueKey]: asNumbers && /^\d+$/.test(key) ? +value : value,
      [textKey]: text,
    };

    if (isPlainObject(source[key]) && source[key].helpText) { item.helpText = source[key].helpText; }
    return item;
  });
};
export const otherOption = (prepick, text) => ({
  value: true,
  text: text || 'Other',
  logical: true,
  logicalValue: prepick || '',
});

export const deepRemoveKeys = (obj, keys = [], testKey = () => false) => {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      if ((Array.isArray(keys) && keys.includes(prop)) || testKey(prop)) {
        delete obj[prop];
      } else if (typeof (obj[prop]) === 'object') {
        deepRemoveKeys(obj[prop], keys, testKey);
      }
    }
  }
};

export const formatName = value => {
  let name = value
    .replace(/^[-, ]+/g, '')
    .replace(/[-, ]+$/g, '')
    .replace(/(\s?-\s?)/g, '-')
    .replace(/  +/g, ' ')
    .replace(/--+/g, '-');

  name = upperFirst(
    name
      .split('')
      .map((letter, i) => / |-/.test(name[i - 1]) ? letter.toUpperCase() : letter.toLowerCase())
      .join(''),
  );

  return name;
};

/**
 * Returns a string with a UK locale representation of this number
 * @param {Number} number
 * @param {String} locale='en-gb'
 * @returns {String} formatted string in the UK locale
 */
export const getNumberFormatted = (number, locale = 'en-gb') => {
  return Number(number).toLocaleString(locale);
};

export const compactNumber = (number) => {
  const formatted = Intl.NumberFormat('en-gb', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number);

  return number > 1000000 ? formatted.toLowerCase() : formatted;
};

export const createAbtastyScript = config => {
  if (
    process.env.NODE_ENV === 'production' &&
    config.abtastyEnabled &&
    config.abtastyId
  ) {
    const scriptElement = document.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.src = `https://try.abtasty.com/${config.abtastyId}.js`;

    document.head.prepend(scriptElement);
  }
};

export const removeAbtastyScript = config => {
  if (
    process.env.NODE_ENV === 'production' &&
    config.abtastyEnabled &&
    config.abtastyId
  ) {
    document.head.removeChild(document.querySelector(`[src="https://try.abtasty.com/${config.abtastyId}.js"]`));
  }
};

export const isNumber = (n) => typeof n === 'number' && isFinite(n);

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export const difference = (object, base) => {
  return transform(object, (result, value, key) => {
    if (!isEqual(value, base[key])) {
      result[key] = isObject(value) && isObject(base[key]) ? difference(value, base[key]) : value;
    }
  });
};

/**
 * @param obj Object                 The object to flatten
 * @param prefix String (Optional)  The prefix to add before each key, also used for recursion
 * @param result
 **/
export const flattenObject = (obj, prefix = false, result = null) => {
  result = result || {};

  // Preserve empty objects and arrays, they are lost otherwise
  if (prefix && typeof obj === 'object' && obj !== null && !Object.keys(obj).length) {
    result[prefix] = Array.isArray(obj) ? [] : {};
    return result;
  }

  prefix = prefix ? prefix + '.' : '';

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Recursion on deeper objects
        flattenObject(obj[key], prefix + key, result);
      } else {
        result[prefix + key] = obj[key];
      }
    }
  }
  return result;
};

/**
* @param obj {Object} The object to unflatten
**/
export const unflattenObject = (obj) => {
  const result = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Just a complicated regex to only match a single dot in the middle of the string
      const keys = key.match(/^\.+[^.]*|[^.]*\.+$|(?:\.{2,}|[^.])+(?:\.+$)?/g);
      keys.reduce((acc, cur, index) => {
        return acc[cur] || (acc[cur] = isNaN(Number(keys[index + 1])) ? (keys.length - 1 === index ? obj[key] : {}) : []);
      }, result);
    }
  }

  return result;
};

export const parseRules = (rules, data) => {
  if (typeof rules === 'string') return rules;

  let parsedRules = {};
  const flattenRules = flattenObject(rules);

  for (const rule in flattenRules) {
    if (flattenRules?.[rule]?.toString().charAt(0) === '@') {
      parsedRules = { ...parsedRules, [rule]: data[flattenRules[rule].slice(1)] || null };
      continue;
    }

    parsedRules = { ...parsedRules, [rule]: flattenRules[rule] };
  }

  return unflattenObject(parsedRules);
};

/**
 * Returns a string for subform label based on its mode (new subform or editing existing subform item)
 * @param {String} itemName - name of subform item
 * @param {Boolean} isSubformInEditMode - toggle 'Edit' or 'Add' label prefix
 * @param {Number|Null} subformIndex - adds index as labels suffix in edit mode
 * @returns {String} subform label string
 */
export const getSubformLabel = (itemName, isSubformInEditMode, subformIndex) => {
  const prefix = isSubformInEditMode ? 'Edit' : 'Add';
  const body = ` ${itemName}`;
  const suffix = subformIndex ? ` #${subformIndex}` : '';

  return `${prefix}${body}${suffix}`;
};

/**
 * Sorting function to make needed order of quote options. In order to task SU-3309.
 * @param optionA - 'included' attribute of item in optionalExtra
 * @param optionB - 'included' attribute of item in optionalExtra
 * @returns {number} sorting result
 */
export const sortQuoteOptions = (optionA, optionB) => {
  const weights = {
    Y: 0,
    extra: 1,
  };

  if (weights[optionA] > weights[optionB]) return 1;
  if (weights[optionA] < weights[optionB]) return -1;

  return 0;
};

/**
 * Return example of postcode
 * @param startsWith {String} - specific letters at the beginning postcode
 * @returns {String} - postcode example
 */
export const getPostcodeExample = (startsWith) => {
  switch (startsWith) {
    case 'BT':
      return 'e.g. BT2 8GD';
    default:
      return 'e.g. AB10 1NP';
  }
};

export const checkImgURL = (url) => {
  return (url.match(/\.(jpeg|jpg|gif|png)$/) != null);
};

export const isQuotezonePartner = (partner) => {
  return PARTNERS.QuoteZone.includes(partner);
};

export const isQzOrCompareNiPartner = (partner) => {
  return PARTNERS.QuoteZone.includes(partner) || PARTNERS.CompareNI.includes(partner);
};

/**
 * Return options for internal trackers
 * @param {Object[]} options
 * @return {Object} internal tracker options
 */
export const createTrackerOptions = (options) => {
  return options.reduce((acc, { value, trackerValue }) => {
    options[value] = trackerValue;
    return options;
  }, {});
};

/**
 * Retrieves field errors based on the specified validation rules
 * @param {string} fieldName - The name of the field for which to retrieve errors.
 * @param {Object} failedRules - An object containing the failed validation rules.
 * @param {Array} fallbackErrors - An array of fallback errors to return if there are no validation errors.
 * @param {Object} context - The context object that holds the data.
 * @returns {Array} An array of field errors.
 */
// Note This function is used for such a parameter as validationErrors, for more information about this parameter, check the file quotezone-form-builder.md and find validationErrors there.
export const retrieveFieldErrors = (fieldName, failedRules, fallbackErrors, context) => {
  if (!context.validationErrors || !context.validationErrors[fieldName]) {
    return fallbackErrors || [];
  }

  const errors = Object.keys(failedRules).map(error => {
    return context.validationErrors[fieldName][error];
  }).filter(error => !!error);

  return errors.length ? errors : (fallbackErrors || []);
};

/**
 * @param {Number} price - price value from BE
 * @param {String} classes - optional classes for decimal part
 * @returns {String} string - template string
 */
export const getPriceTemplate = (price, classes) => {
  const priceParts = price.toFixed(2).toString().split('.');
  return `&pound;${priceParts[0]}<span class="${classes}">.${priceParts[1]}</span>`;
};

/**
 * @param {String} email - entered email by user
 * @returns {String} suggested correct email
 */
export const suggestedEmail = (
  email,
) => {
  if (!emailRegex.test(email)) return null;

  const name = email.split('@').shift();
  const domain = email.split('@').pop();
  const parsedDomain = parseDomain(domain);

  if (parsedDomain.type !== ParseResultType.NotListed) return null;

  const domainLabels = [...parsedDomain.labels];
  const topLevelDomain = domainLabels.pop().toLowerCase();
  const emailPart = `${name}@${domainLabels.join('.')}`;
  const correction = TOP_LEVEL_DOMAIN_TYPOS.find(c => c.typo.includes(topLevelDomain));

  if (correction) {
    return `${emailPart}.${correction.suggestion}`;
  }

  if (topLevelDomain.includes('com')) {
    return `${emailPart}.com`;
  }

  if (domainLabels[domainLabels.length - 1] === 'co') {
    domainLabels.pop();
    return `${name}@${domainLabels.join('.')}.co.uk`;
  }

  return null;
};

export const getStructure = (data) => {
  return data.steps.map(step => ({
    stepId: step.stepId,
    stepLabel: step.stepLabel,
    groups: step.questions.reduce((groups, question) => {
      const groupIndex = groups.findIndex(g => g.groupId === question.groupId);
      const fieldIds = Array.isArray(question.params?.customFields)
        ? question.params.customFields
        : [question.params?.customFields?.field || question.questionId];

      if (groupIndex !== -1) {
        groups[groupIndex].sections.push({
          sectionId: question.questionId,
          sectionLabel: question.params?.title || question.questionId,
          sectionLabelAdditional: question.params?.titleAdditional,
          fields: fieldIds,
        });
      } else {
        groups.push({
          groupId: question.groupId,
          groupLabel: question.groupLabel,
          sections: [{
            sectionId: question.questionId,
            sectionLabel: question.params?.title || question.questionId,
            sectionLabelAdditional: question.params?.titleAdditional,
            fields: fieldIds,
          }],
        });
      }

      return groups;
    }, []),
  }));
};

/**
 * Generates a preview list from the data array based on the provided mappings(Claims and Convictions).
 *
 * @param {Array} dataArray - Array of data objects to process.
 * @param {string|number} itemId - The ID of the item to match in the data array.
 * @param {Object} mappings - Field mappings with prepend, value, and append configuration.
 * @returns {Array} - The generated preview list.
 */
export const generateClaimsConvictionsPreviewList = (dataArray, itemId, mappings) => {
  const data = dataArray.find(entry => entry.id === itemId);

  if (!data) {
    return [];
  }

  return Object.entries(mappings).reduce((acc, [field, config]) => {
    const fieldExists = Object.prototype.hasOwnProperty.call(data, field);
    const isFieldValid = data[field] !== null && data[field] !== undefined;
    const condition = !config.condition || config.condition();

    if (fieldExists && isFieldValid && condition) {
      acc.push({
        prepend: config.prepend,
        value: config.value,
        append: config.append,
      });
    }

    return acc;
  }, []);
};

/**
 * Maps custom fields from the provided questions to an object where each custom field
 * is a key, and the value is either the default value (if available) or `null`.
 *
 * @param {Array<Object>} questions - An array of question objects, each containing params.
 * @returns {Object} - An object where keys are custom field names and values are either the default value or `null`.
 *
 */

export const mapCustomFieldsToDefaults = (questions) => {
  return questions.reduce((acc, { params }) => {
    if (params?.hiddenForAdditionals) return acc;

    Object.values(params.customFields).forEach(field => {
      acc[field] = params?.defaultValues?.[field] ?? null;
    });

    return acc;
  }, {});
};

/**
 * Returns a string that will be used in page's URL instead of default productName.
 *
 * @param {string} productName - The name of the product to check.
 * @param {boolean} isLeadgen - a flag that defines what type of form it is: core or leadgen
 * @returns {string} - New prefix or the original product name if no transformation is needed.
 */
export const getPageUrlPrefix = (productName, isLeadgen) => {
  // set 'motorhomequote' prefix for core motorhome in router.js
  if (isLeadgen === false && productName === 'motorhome') return 'motorhomequote';
  // set 'motorhomequote' prefix for core motorhome in api.js, because we pass 'apiProductName' from api.js in this case
  if (productName === 'motorhomeagg') return 'motorhomequote';

  return productName;
};

export const getAdditionalIcon = (title) => {
  switch (title) {
    case 'Dr':
    case 'Mx':
      return 'mdi-account-outline';
    case 'Mr':
      return 'mdi-face-man';
    default:
      return 'mdi-face-woman';
  }
};
// Check if a value is empty (undefined, null, or empty string)
export const isEmpty = (value) => [undefined, null, ''].includes(value);

// Check if a given date is valid using UtcDate
export const isValidDate = (value) => {
  const date = new UtcDate(value);
  return date.isValid();
};

// Calculate the age based on birth date and a reference date
export const getAge = (birthDate, referenceDate) => {
  const birth = typeof birthDate === 'string' ? new UtcDate(birthDate) : birthDate;
  const refDate = typeof referenceDate === 'string' ? new UtcDate(referenceDate) : referenceDate;
  return refDate.differenceInYears(birth);
};

// Return failed validation result
export const failedValidation = {
  valid: false,
  required: true,
};

export const calculateAge = (dateOfBirth, dateOfInsuranceStart, dateOfInsuranceStartMaxDays) => {
  const birthDate = isValidDate(dateOfBirth) ? new UtcDate(dateOfBirth) : null;

  const today = new UtcDate();
  const dateOfInsuranceStartDate = new UtcDate(dateOfInsuranceStart);
  const maxStartDate = today.addDays(dateOfInsuranceStartMaxDays);
  const insuranceStartDate = dateOfInsuranceStartDate.isValid() ? dateOfInsuranceStartDate : maxStartDate;

  const age = birthDate ? getAge(birthDate, insuranceStartDate) : null;

  return {
    today,
    dateOfInsuranceStartDate,
    maxStartDate,
    insuranceStartDate,
    age,
  };
};
